import * as React from "react"
import { Loader2, Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { formatAmount } from "@/lib/format"
import { BOOK_LABELS, type BookId } from "@/lib/types"
import { cn } from "@/lib/utils"

import { ACCOUNT_NAMES, type AdjustmentEntryPayload } from "@/routes/books/api"
import type { AccountOption } from "@/routes/books/mapping"
import { t } from "@/lib/i18n"

const MIN_REASON_LENGTH = 10

interface LineDraft {
  accountCode: string
  debit: string
  credit: string
  costCenter: string
}

const EMPTY_LINE: LineDraft = { accountCode: "", debit: "", credit: "", costCenter: "" }

interface AdjustmentEntryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultBook: BookId
  /**
   * El plan de cuentas REAL (`GET /ledger/accounts`).
   *
   * No es un detalle de comodidad: el backend resuelve la linea por el CODIGO
   * contra `account_local`, y el catalogo sembrado llega a la divisionaria de 3
   * digitos ("639"). La lista fija que usaba este dialogo tenia subdivisionarias
   * de 5 ("63911") que no existen, asi que TODO asiento posteado desde aqui
   * moria con "cuenta desconocida". Sin cuentas cargadas se cae a la lista fija,
   * que es la correcta en modo mock.
   */
  accounts?: AccountOption[]
  pending?: boolean
  onSubmit: (payload: AdjustmentEntryPayload) => void | Promise<void>
}

/**
 * Asiento manual de ajuste o reclasificacion.
 * Un sistema donde el contador no puede hacer un asiento de ajuste no lo usa ningun contador.
 * Motivo obligatorio y cuadre exigido antes de postear.
 */
export function AdjustmentEntryDialog({
  open,
  onOpenChange,
  defaultBook,
  accounts: accountsProp,
  pending,
  onSubmit,
}: AdjustmentEntryDialogProps) {
  const [book, setBook] = React.useState<BookId>(defaultBook)
  const [date, setDate] = React.useState("2026-07-31")
  const [glosa, setGlosa] = React.useState("")
  const [lines, setLines] = React.useState<LineDraft[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }])
  const [reason, setReason] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setBook(defaultBook)
      setGlosa("")
      setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }])
      setReason("")
      setTouched(false)
    }
  }, [open, defaultBook])

  const accounts = React.useMemo(
    () =>
      accountsProp && accountsProp.length > 0
        ? accountsProp
        : Object.entries(ACCOUNT_NAMES).map(([code, name]) => ({ code, name })),
    [accountsProp],
  )

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0)
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0)
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005 && totalDebit > 0
  const linesValid = lines.every(
    (l) => !l.accountCode || (Number(l.debit) || 0) === 0 || (Number(l.credit) || 0) === 0,
  )
  const filledLines = lines.filter(
    (l) => l.accountCode && ((Number(l.debit) || 0) > 0 || (Number(l.credit) || 0) > 0),
  )
  const reasonInvalid = reason.trim().length < MIN_REASON_LENGTH
  const canSubmit =
    balanced && linesValid && filledLines.length >= 2 && glosa.trim().length > 3 && !reasonInvalid

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))
  }

  async function handleSubmit() {
    setTouched(true)
    if (!canSubmit) return
    await onSubmit({
      book,
      date,
      glosa: glosa.trim(),
      reason: reason.trim(),
      lines: filledLines.map((l) => ({
        accountCode: l.accountCode,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        costCenter: l.costCenter || undefined,
      })),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("Nuevo asiento de ajuste")}</DialogTitle>
          <DialogDescription>
            {t("Provisiones, depreciacion, diferencia de cambio, reclasificaciones. Se postea solo si cuadra y con motivo registrado.")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="asiento-libro">{t("Libro")}</Label>
            <Select value={book} onValueChange={(value) => setBook(value as BookId)}>
              <SelectTrigger id="asiento-libro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(BOOK_LABELS) as BookId[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {BOOK_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="asiento-fecha">{t("Fecha")}</Label>
            <SelectorDeFecha id="asiento-fecha" value={date} onChange={setDate} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="asiento-glosa">{t("Glosa")}</Label>
            <Input
              id="asiento-glosa"
              value={glosa}
              onChange={(event) => setGlosa(event.target.value)}
              placeholder={t("Provision de vacaciones julio")}
            />
          </div>
        </div>

        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_100px_100px_90px_28px] gap-1 text-2xs font-semibold uppercase text-muted-foreground">
            <span>{t("Cuenta")}</span>
            <span className="text-right">{t("Debe")}</span>
            <span className="text-right">{t("Haber")}</span>
            <span>{t("C. costo")}</span>
            <span />
          </div>
          {lines.map((line, index) => (
            <div key={index} className="grid grid-cols-[1fr_100px_100px_90px_28px] gap-1">
              <Select
                value={line.accountCode}
                onValueChange={(value) => updateLine(index, { accountCode: value })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={t("Cuenta")} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.code} value={account.code}>
                      <span className="code">{account.code}</span> · {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-8 text-right font-mono text-xs"
                inputMode="decimal"
                value={line.debit}
                onChange={(event) => updateLine(index, { debit: event.target.value })}
              />
              <Input
                className="h-8 text-right font-mono text-xs"
                inputMode="decimal"
                value={line.credit}
                onChange={(event) => updateLine(index, { credit: event.target.value })}
              />
              {/* El centro de costo NO viaja: `POST /ledger/entries` construye
                  cada linea como (cuenta, debe, haber, glosa) y descartaria la
                  clave en silencio. Se deja visible para no rehacer el formulario
                  el dia que el backend lo acepte, y se dice abajo que no se
                  guarda. TODO(backend): cost_center en las lineas del asiento. */}
              <Input
                className="h-8 text-xs"
                value={line.costCenter}
                onChange={(event) => updateLine(index, { costCenter: event.target.value })}
                placeholder={t("CC-ADM")}
                title={t("Todavia no se guarda: el backend no acepta centro de costo por linea.")}
              />
              <Button
                variant="ghost"
                size="iconSm"
                onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                disabled={lines.length <= 2}
                aria-label={t("Eliminar linea")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="xs"
            onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
          >
            <Plus />
            {t("Agregar linea")}</Button>
        </div>

        {/* Cuadre en vivo: mientras no cuadre, el boton de postear no se habilita. */}
        <div
          className={cn(
            "sticky bottom-0 z-10 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border-2 px-2.5 py-1.5",
            balanced
              ? "border-success/35 bg-success-soft"
              : "hatch-alert border-destructive/60 bg-destructive-soft",
          )}
        >
          <span
            className={cn(
              "text-xs font-semibold",
              balanced ? "text-success" : "uppercase tracking-[0.04em] text-destructive",
            )}
          >
            {balanced ? "Asiento cuadrado" : "El asiento no cuadra"}
          </span>
          <dl className="ml-auto flex items-end gap-4 text-2xs">
            <div className="text-right">
              <dt className="eyebrow">{t("Debe")}</dt>
              <dd className="num font-semibold text-foreground">{formatAmount(totalDebit)}</dd>
            </div>
            <div className="text-right">
              <dt className="eyebrow">{t("Haber")}</dt>
              <dd className="num font-semibold text-foreground">{formatAmount(totalCredit)}</dd>
            </div>
            <div
              className={cn(
                "border-l pl-4 text-right",
                balanced ? "border-success/25" : "border-destructive/30",
              )}
            >
              <dt className={cn("eyebrow", !balanced && "text-destructive/80")}>{t("Diferencia")}</dt>
              <dd
                className={cn(
                  "num font-semibold leading-none",
                  balanced ? "text-sm text-success" : "text-lg text-destructive",
                )}
              >
                {formatAmount(Math.abs(totalDebit - totalCredit))}
              </dd>
            </div>
          </dl>
        </div>

        <div className="space-y-1">
          <Label htmlFor="asiento-motivo">
            {t("Motivo")}{" "}<span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="asiento-motivo"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("Por que se registra este ajuste y con que sustento.")}
            className="min-h-[4rem]"
            aria-invalid={touched && reasonInvalid}
          />
          <p className="text-2xs text-muted-foreground">
            {t("Obligatorio. El asiento queda con autor, fecha y motivo en el audit log. El centro de costo por linea todavia no se guarda: el backend no lo acepta en el asiento manual.")}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("Cancelar")}</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit || pending}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t("Postear asiento")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
